'use client';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useSymbolSearch } from '@/hooks/use-symbol-search';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface CreateAssetWizardProps {
  onSubmit: (data: any) => void;
  isLoading?: boolean;
}

export function CreateAssetWizard({ onSubmit, isLoading = false }: CreateAssetWizardProps) {
  const [formData, setFormData] = useState({
    name: '',
    tokenSymbol: '',
    comment: '',
    category: 'crypto' as 'crypto' | 'stock' | 'commodity' | 'index',
    isMajor: false,
    marketCap: ''
  });

  const updateFormData = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Search</Label>
          <SymbolSearchField
            onSelect={(match) => {
              updateFormData('tokenSymbol', match.symbol);
              updateFormData('name', match.name);
            }}
          />
          <p className="mt-1 text-muted-foreground text-sm">
            Search Alpha Vantage and auto-fill symbol and name
          </p>
        </div>

        <div>
          <Label htmlFor="name">Asset Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            required
            placeholder="e.g., Bitcoin"
          />
        </div>

        <div>
          <Label htmlFor="tokenSymbol">Symbol</Label>
          <Input
            id="tokenSymbol"
            value={formData.tokenSymbol}
            onChange={(e) => updateFormData('tokenSymbol', e.target.value)}
            required
            placeholder="e.g., BTC"
            className="uppercase"
          />
          <p className="mt-1 text-muted-foreground text-sm">Ticker symbol for the asset</p>
        </div>

        <div>
          <Label htmlFor="category">Category</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => updateFormData('category', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="crypto">Crypto</SelectItem>
              <SelectItem value="stock">Stock</SelectItem>
              <SelectItem value="commodity">Commodity</SelectItem>
              <SelectItem value="index">Index</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="marketCap">Market Cap (Optional)</Label>
          <Input
            id="marketCap"
            type="number"
            value={formData.marketCap}
            onChange={(e) => updateFormData('marketCap', e.target.value)}
            placeholder="e.g., 1000000000000"
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isMajor"
            checked={formData.isMajor}
            onChange={(e) => updateFormData('isMajor', e.target.checked)}
            className="rounded border-gray-300"
          />
          <Label htmlFor="isMajor">Is this a major asset?</Label>
        </div>

        <div>
          <Label htmlFor="comment">Comment (Optional)</Label>
          <Textarea
            id="comment"
            value={formData.comment}
            onChange={(e) => updateFormData('comment', e.target.value)}
            placeholder="Additional notes about this asset..."
            rows={3}
          />
        </div>
      </div>

      <div className="flex gap-4">
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Asset
        </Button>
      </div>
    </form>
  );
}

function SymbolSearchField({
  onSelect
}: { onSelect: (m: { symbol: string; name: string }) => void }) {
  const { query, setQuery, results, loading } = useSymbolSearch();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (triggerRef.current && open) {
      const width = triggerRef.current.offsetWidth;
      setPopoverWidth(width);
    }
  }, [open]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim().length >= 2) {
      setOpen(true);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          className="w-full justify-between"
          aria-expanded={open}
        >
          {query ? query : 'Search symbol or company...'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={popoverWidth ? { width: `${popoverWidth}px` } : undefined}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type to search (min 2 characters)..."
            value={query}
            onValueChange={handleQueryChange}
            className="w-full"
          />
          <CommandList>
            {query.trim().length < 2 && (
              <div className="p-3 text-center text-muted-foreground text-sm">
                Type at least 2 characters to search...
              </div>
            )}
            {loading && query.trim().length >= 2 && (
              <div className="p-3 text-center text-muted-foreground text-sm">Searching...</div>
            )}
            {!loading && results.length === 0 && query.trim().length >= 2 && (
              <CommandEmpty>No results found.</CommandEmpty>
            )}
            {!loading && results.length > 0 && (
              <CommandGroup>
                {results.map((m) => (
                  <CommandItem
                    key={`${m.symbol}-${m.name}`}
                    value={`${m.symbol} ${m.name}`}
                    onSelect={() => {
                      onSelect({ symbol: m.symbol, name: m.name });
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('mr-2 h-4 w-4 opacity-0')} />
                    <span className="font-medium">{m.symbol}</span>
                    <span className="ml-2 text-muted-foreground">{m.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
